(() => {
  const SELECTOR = '[data-pk-section]';

  function init(root) {
    if (!root || root.dataset.pkInitialized === 'true') return;
    root.dataset.pkInitialized = 'true';

    const stage = root.querySelector('[data-pk-gallery-stage]');
    const slides = Array.from(root.querySelectorAll('[data-pk-media-id]'));
    const thumbnails = Array.from(root.querySelectorAll('[data-pk-thumbnail]'));
    const prev = root.querySelector('[data-pk-prev]');
    const next = root.querySelector('[data-pk-next]');
    let activeIndex = Math.max(0, slides.findIndex((slide) => !slide.hidden));

    function stopInactiveMedia(activeSlide) {
      slides.forEach((slide) => {
        if (slide === activeSlide) return;
        slide.querySelectorAll('video').forEach((video) => video.pause());
        slide.querySelectorAll('iframe').forEach((frame) => {
          if (frame.src) frame.src = frame.src;
        });
      });
    }

    function activateMedia(target) {
      if (!slides.length) return;
      let index = typeof target === 'number'
        ? target
        : slides.findIndex((slide) => slide.dataset.pkMediaId === String(target));

      if (index < 0) return;
      activeIndex = (index + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.hidden = !isActive;
        slide.setAttribute('aria-hidden', String(!isActive));
      });

      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const isActive = thumbnailIndex === activeIndex;
        thumbnail.setAttribute('aria-current', String(isActive));
        if (isActive) thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      });

      stopInactiveMedia(slides[activeIndex]);
    }

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => activateMedia(index));
    });

    prev?.addEventListener('click', () => activateMedia(activeIndex - 1));
    next?.addEventListener('click', () => activateMedia(activeIndex + 1));

    stage?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        activateMedia(activeIndex - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        activateMedia(activeIndex + 1);
      }
    });

    if (stage && slides.length > 1) {
      let pointerStart = null;
      stage.addEventListener('pointerdown', (event) => {
        if (event.target.closest('video, iframe, model-viewer, button')) return;
        pointerStart = event.clientX;
      });
      stage.addEventListener('pointerup', (event) => {
        if (pointerStart === null) return;
        const delta = event.clientX - pointerStart;
        pointerStart = null;
        if (Math.abs(delta) < 48) return;
        activateMedia(activeIndex + (delta < 0 ? 1 : -1));
      });
      stage.addEventListener('pointercancel', () => { pointerStart = null; });
    }

    const variantSelector = root.querySelector('[data-pk-variant-select]');
    const variantInput = root.querySelector('[data-pk-variant-id]');
    const price = root.querySelector('[data-pk-price]');
    const comparePrice = root.querySelector('[data-pk-compare-price]');
    const addButton = root.querySelector('[data-pk-add-button]');
    const isPrelaunch = root.dataset.pkPrelaunch === 'true';

    function updateVariant() {
      if (!variantSelector) return;
      const option = variantSelector.options[variantSelector.selectedIndex];
      if (!option) return;

      if (variantInput) variantInput.value = option.value;
      if (price) price.textContent = option.dataset.price || price.textContent;

      if (comparePrice) {
        comparePrice.textContent = option.dataset.comparePrice || '';
        comparePrice.hidden = !option.dataset.comparePrice;
      }

      if (addButton) {
        const available = option.dataset.available === 'true';
        addButton.disabled = isPrelaunch || !available;
        addButton.type = addButton.disabled ? 'button' : 'submit';
        addButton.textContent = isPrelaunch
          ? addButton.dataset.prelaunchLabel
          : available
            ? addButton.dataset.liveLabel
            : addButton.dataset.soldoutLabel;
      }

      if (option.dataset.mediaId) activateMedia(option.dataset.mediaId);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', option.value);
      window.history.replaceState({}, '', url);
    }

    variantSelector?.addEventListener('change', updateVariant);

    const quantityInput = root.querySelector('[data-pk-quantity]');
    root.querySelectorAll('[data-pk-quantity-change]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!quantityInput) return;
        const delta = Number(button.dataset.pkQuantityChange || 0);
        const min = Number(quantityInput.min || 1);
        const max = quantityInput.max ? Number(quantityInput.max) : Infinity;
        const current = Number(quantityInput.value || min);
        quantityInput.value = String(Math.min(max, Math.max(min, current + delta)));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    quantityInput?.addEventListener('change', () => {
      const min = Number(quantityInput.min || 1);
      const max = quantityInput.max ? Number(quantityInput.max) : Infinity;
      const current = Number(quantityInput.value || min);
      quantityInput.value = String(Math.min(max, Math.max(min, Number.isFinite(current) ? current : min)));
    });

    const countdown = root.querySelector('[data-pk-countdown]');
    if (countdown && isPrelaunch) {
      const releaseAt = Date.parse(countdown.dataset.releaseAt || '');
      const fields = {
        days: countdown.querySelector('[data-pk-days]'),
        hours: countdown.querySelector('[data-pk-hours]'),
        minutes: countdown.querySelector('[data-pk-minutes]'),
        seconds: countdown.querySelector('[data-pk-seconds]')
      };
      let reloading = false;

      function updateCountdown() {
        if (!Number.isFinite(releaseAt)) return;
        const remaining = releaseAt - Date.now();
        if (remaining <= 0) {
          Object.values(fields).forEach((field) => { if (field) field.textContent = '00'; });
          if (!reloading) {
            reloading = true;
            window.setTimeout(() => window.location.reload(), 750);
          }
          return;
        }

        const days = Math.floor(remaining / 86400000);
        const hours = Math.floor((remaining % 86400000) / 3600000);
        const minutes = Math.floor((remaining % 3600000) / 60000);
        const seconds = Math.floor((remaining % 60000) / 1000);
        if (fields.days) fields.days.textContent = String(days).padStart(2, '0');
        if (fields.hours) fields.hours.textContent = String(hours).padStart(2, '0');
        if (fields.minutes) fields.minutes.textContent = String(minutes).padStart(2, '0');
        if (fields.seconds) fields.seconds.textContent = String(seconds).padStart(2, '0');
      }

      updateCountdown();
      window.setInterval(updateCountdown, 1000);
    }
  }

  function initAll(scope = document) {
    scope.querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
